import React, { useState, useEffect, useCallback } from 'react';
import {
    Paper,
    Typography,
    Button,
    Box,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Chip,
    InputAdornment,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    AccessTime as AccessTimeIcon,
    Update as UpdateIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useAPI from 'hooks/useAPI.js';
import PatientService from 'services/PatientService.js';
import { useParams, useOutletContext } from "react-router-dom";
import moment from 'moment';
import TranslatableInput from "components/inputs/TranslatableInput";

function PatientAlerts({ patientId, hospitalId, onView }) {
    // Use props if provided, otherwise fall back to useParams/useOutletContext
    const paramsPatientId = useParams().patientId;
    const outletHospitalId = useOutletContext()[0];
    const finalPatientId = patientId || paramsPatientId;
    const finalHospitalId = hospitalId || outletHospitalId;

    const theme = useTheme();
    const [alerts, setAlerts] = useState([]);
    const [filteredAlerts, setFilteredAlerts] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingAlert, setEditingAlert] = useState(null);
    const [formData, setFormData] = useState({
        severity: 'LOW',
        message: ''
    });
    const [filters, setFilters] = useState({
        severity: '',
        search: ''
    });

    function getAlertsHandler(response) {
        setAlerts(response.data || []);
    }

    // API hooks
    const getAlertsAPI = useAPI(PatientService.getPatientAlerts, getAlertsHandler);
    const addAlertAPI = useAPI(PatientService.addPatientAlert, handleAddSuccess, true);
    const updateAlertAPI = useAPI(PatientService.updatePatientAlert, handleUpdateSuccess, true);
    const deleteAlertAPI = useAPI(PatientService.deletePatientAlert, handleDeleteSuccess, true);

    // Data loading
    const loadAlerts = useCallback(() => {
        getAlertsAPI.execute(finalHospitalId, finalPatientId);
    }, [getAlertsAPI, finalHospitalId, finalPatientId]);

    // Filtering
    const applyFilters = useCallback(() => {
        let filtered = [...alerts];

        if (filters.severity) {
            filtered = filtered.filter(alert => alert.severity === filters.severity);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(alert =>
                alert.message.toLowerCase().includes(searchTerm) ||
                alert.createdByUserFullName.toLowerCase().includes(searchTerm) ||
                alert.lastUpdatedByUserFullName.toLowerCase().includes(searchTerm)
            );
        }

        setFilteredAlerts(filtered);
    }, [alerts, filters]);

    useEffect(() => {
        loadAlerts();
    }, [finalHospitalId, finalPatientId]);

    // Mark notifications as read when this component is viewed
    useEffect(() => {
        if (onView) {
            onView();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        applyFilters();
    }, [alerts, filters, applyFilters]);

    // CRUD Success handlers
    function handleAddSuccess() {
        setOpenDialog(false);
        resetForm();
        loadAlerts();
    }

    function handleUpdateSuccess() {
        setOpenDialog(false);
        resetForm();
        loadAlerts();
    }

    function handleDeleteSuccess() {
        loadAlerts();
    }

    // Form operations
    function handleSubmit() {
        if (editingAlert) {
            updateAlertAPI.execute(finalHospitalId, finalPatientId, editingAlert.id, formData);
        } else {
            addAlertAPI.execute(finalHospitalId, finalPatientId, formData);
        }
    }

    function handleEdit(alert) {
        setEditingAlert(alert);
        setFormData({
            severity: alert.severity,
            message: alert.message
        });
        setOpenDialog(true);
    }

    function handleDelete(alertId) {
        if (window.confirm('Are you sure you want to delete this alert?')) {
            deleteAlertAPI.execute(finalHospitalId, finalPatientId, alertId);
        }
    }

    function resetForm() {
        setFormData({ severity: 'LOW', message: '' });
        setEditingAlert(null);
    }

    function handleCloseDialog() {
        setOpenDialog(false);
        resetForm();
    }

    function clearFilters() {
        setFilters({ severity: '', search: '' });
    }

    // UI helpers
    function getSeverityColor(severity) {
        switch (severity) {
            case 'CRITICAL': return theme.palette.error.main;
            case 'HIGH': return '#FF6B35';
            case 'MODERATE': return theme.palette.warning.main;
            case 'LOW': return theme.palette.info.main;
            default: return theme.palette.grey[500];
        }
    }

    function getSeverityIcon(severity) {
        switch (severity) {
            case 'CRITICAL':
            case 'HIGH':
                return <ErrorIcon sx={{ color: getSeverityColor(severity) }} />;
            case 'MODERATE':
                return <WarningIcon sx={{ color: getSeverityColor(severity) }} />;
            case 'LOW':
                return <InfoIcon sx={{ color: getSeverityColor(severity) }} />;
            default:
                return <InfoIcon />;
        }
    }

    function formatDateTime(dateTimeString) {
        if (!dateTimeString) return 'N/A';

        try {
            // Parse the date string using moment with explicit format - format "YYYY-MM-DD hh:mm a"
            // This works reliably across all browsers including Safari on iOS/iPad
            // Parse as UTC first, then convert to local time
            const date = moment.utc(dateTimeString, "YYYY-MM-DD hh:mm a").local();

            if (!date.isValid()) {
                // Fallback: try parsing as ISO format or other common formats
                const fallbackDate = moment(dateTimeString);
                if (!fallbackDate.isValid()) {
                    return dateTimeString;
                }
                return fallbackDate.fromNow();
            }

            const now = moment();
            const diffInMinutes = now.diff(date, 'minutes');
            const diffInHours = now.diff(date, 'hours');
            const diffInDays = now.diff(date, 'days');

            // If it's today
            if (diffInDays === 0) {
                if (diffInMinutes < 1) {
                    return 'Just now';
                } else if (diffInMinutes < 60) {
                    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
                } else {
                    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
                }
            }
            // If it's yesterday
            else if (diffInDays === 1) {
                return `Yesterday at ${date.format('h:mm A')}`;
            }
            // If it's within the last week
            else if (diffInDays < 7) {
                return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago at ${date.format('h:mm A')}`;
            }
            // If it's older than a week
            else {
                return date.format('MMM D, YYYY h:mm A');
            }
        } catch (error) {
            // Fallback to original string if parsing fails
            return dateTimeString;
        }
    }

    const hasActiveFilters = filters.severity || filters.search;

    return (
        <Paper elevation={0} sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" color="primary" fontWeight="bold">
                        Patient Alerts
                    </Typography>
                    <Chip
                        label={`${filteredAlerts.length} Alert${filteredAlerts.length !== 1 ? 's' : ''}`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>

                {/* Filters */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '8px'
                }}>
                    <TextField
                        size="small"
                        placeholder="Search alerts, messages, or usernames..."
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 200 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Severity</InputLabel>
                        <Select
                            value={filters.severity}
                            label="Filter by Severity"
                            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                            startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
                        >
                            <MenuItem value="">All Severities</MenuItem>
                            <MenuItem value="CRITICAL">Critical</MenuItem>
                            <MenuItem value="HIGH">High</MenuItem>
                            <MenuItem value="MODERATE">Moderate</MenuItem>
                            <MenuItem value="LOW">Low</MenuItem>
                        </Select>
                    </FormControl>

                    {hasActiveFilters && (
                        <Button
                            size="small"
                            startIcon={<ClearIcon />}
                            onClick={clearFilters}
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </Box>

                {/* Add Alert Button */}
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '16px',
                        py: 1.5,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.main + '08'
                        }
                    }}
                >
                    Add Alert
                </Button>

                {/* Alerts List */}
                <Stack spacing={2}>
                    {filteredAlerts.length === 0 ? (
                        <Box sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme.palette.text.secondary
                        }}>
                            <Typography variant="body1">
                                {alerts.length === 0 ? 'No alerts found' : 'No alerts match your filters'}
                            </Typography>
                        </Box>
                    ) : (
                        filteredAlerts.map((alert) => (
                            <Paper
                                key={alert.id}
                                elevation={0}
                                sx={{
                                    border: `2px solid transparent`,
                                    borderRadius: '12px',
                                    backgroundColor: theme.palette.background.paper,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: `0 8px 32px ${getSeverityColor(alert.severity)}20`,
                                        borderColor: getSeverityColor(alert.severity) + '30',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: `linear-gradient(90deg, ${getSeverityColor(alert.severity)}, ${getSeverityColor(alert.severity)}80)`,
                                    }
                                }}
                            >
                                <Box sx={{ p: 3 }}>
                                    {/* Header with severity and actions */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '8px',
                                                    backgroundColor: getSeverityColor(alert.severity) + '15',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {getSeverityIcon(alert.severity)}
                                            </Box>
                                            <Box>
                                                <Chip
                                                    label={alert.severity}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getSeverityColor(alert.severity) + '20',
                                                        color: getSeverityColor(alert.severity),
                                                        fontWeight: 'bold',
                                                        fontSize: '0.75rem',
                                                        height: '24px',
                                                        borderRadius: '6px'
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(alert)}
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.main + '10'
                                                    }
                                                }}
                                                title="Edit Alert"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(alert.id)}
                                                sx={{
                                                    color: theme.palette.error.main,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.error.main + '10'
                                                    }
                                                }}
                                                title="Delete Alert"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {/* Alert Message */}
                                    <Typography
                                        variant="body1"
                                        color="text.primary"
                                        sx={{
                                            mb: 3,
                                            lineHeight: 1.6,
                                            fontSize: '1rem',
                                            fontWeight: 500
                                        }}
                                    >
                                        {alert.message}
                                    </Typography>

                                    {/* Metadata Section */}
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 2,
                                        pt: 2,
                                        borderTop: `1px solid ${theme.palette.divider}`
                                    }}>
                                        {/* Created Info */}
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <AccessTimeIcon sx={{ fontSize: '16px', color: theme.palette.text.secondary }} />
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                                                    CREATED
                                                </Typography>
                                            </Box>
                                            <Tooltip title={`Exact time: ${alert.createdAt}`} arrow>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.primary,
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}>
                                                    {formatDateTime(alert.createdAt)}
                                                </Typography>
                                            </Tooltip>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: '14px', color: theme.palette.text.secondary }} />
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                    by {alert.createdByUserFullName}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Last Updated Info */}
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <UpdateIcon sx={{ fontSize: '16px', color: theme.palette.text.secondary }} />
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                                                    LAST UPDATED
                                                </Typography>
                                            </Box>
                                            <Tooltip title={`Exact time: ${alert.lastUpdatedAt}`} arrow>
                                                <Typography variant="body2" sx={{
                                                    color: theme.palette.text.primary,
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline'
                                                    }
                                                }}>
                                                    {formatDateTime(alert.lastUpdatedAt)}
                                                </Typography>
                                            </Tooltip>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PersonIcon sx={{ fontSize: '14px', color: theme.palette.text.secondary }} />
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                    by {alert.lastUpdatedByUserFullName}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        ))
                    )}
                </Stack>
            </Stack>

            {/* Add/Edit Alert Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '12px' }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {editingAlert ? 'Edit Alert' : 'Add New Alert'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel>Severity Level</InputLabel>
                            <Select
                                value={formData.severity}
                                label="Severity Level"
                                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                            >
                                <MenuItem value="LOW">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ color: getSeverityColor('LOW') }} />
                                        Low
                                    </Box>
                                </MenuItem>
                                <MenuItem value="MODERATE">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WarningIcon sx={{ color: getSeverityColor('MODERATE') }} />
                                        Moderate
                                    </Box>
                                </MenuItem>
                                <MenuItem value="HIGH">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ErrorIcon sx={{ color: getSeverityColor('HIGH') }} />
                                        High Priority
                                    </Box>
                                </MenuItem>
                                <MenuItem value="CRITICAL">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ErrorIcon sx={{ color: getSeverityColor('CRITICAL') }} />
                                        Critical
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <TranslatableInput
                            value={formData.message}
                            name="message"
                            onTranslated={(text) => setFormData({ ...formData, message: text })}
                        >
                        <TextField
                            fullWidth
                            label="Message"
                            multiline
                            rows={4}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            placeholder="Enter message here..."
                            inputProps={{ maxLength: 500 }}
                        />
                        </TranslatableInput>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.message.trim() || addAlertAPI.status === 'pending' || updateAlertAPI.status === 'pending'}
                        sx={{ minWidth: 100 }}
                    >
                        {(addAlertAPI.status === 'pending' || updateAlertAPI.status === 'pending')
                            ? 'Saving...'
                            : editingAlert ? 'Update Alert' : 'Add Alert'
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default PatientAlerts;